import { delayFetchMessageAttachments } from 'core_main/api/messages';
import { updateSessionMessages } from 'core_main/api/sessions';
import { debug, handleError } from 'globals/errors';
import { KustomerErrorResponse } from 'globals/types';
import Pubnub from 'pubnub';
import { runListenersForOnMessageReceivedEvent } from '../helpers/chatMessageReceived';
import { getMetaPubNub } from '../helpers/instance';

// Track processed messages to prevent duplicate unread count increments
// Key format: messageId:timetoken to handle duplicate PubNub events
const processedMessages = new Set<string>();

// Export function to reset processed messages for testing
export const resetProcessedMessages = () => {
  processedMessages.clear();
};

export const markMessagesAsDelivered = (
  messageEvents: any[],
  sessionId: string,
) => {
  const messageIds: string[] = [];

  messageEvents.forEach((messageEvent) => {
    const { timetoken: messageTimetoken, channel, message } = messageEvent;

    const { id } = message.data;

    messageIds.push(id);

    // best-effort delivery marker; optional-chaining may short-circuit to undefined so guard before catching
    const addMessageActionResult = getMetaPubNub()?.instance?.addMessageAction({
      channel,
      messageTimetoken,
      action: {
        type: 'delivery',
        value: 'success',
      },
    });
    addMessageActionResult?.catch?.((error: any) => {
      // a duplicate action after a merge 409s and must not escape as an unhandled rejection;
      // PubNub rejections carry the HTTP code at error.status.statusCode (a number)
      if (error?.status?.statusCode === 409) {
        debug({ message: 'Message action already exists; skipping' });
        return;
      }
      handleError(error, { message: 'Adding message action' });
    });
  });

  const updateResult = updateSessionMessages({
    sessionId,
    messageIds,
    body: { delivered: true },
  });
  updateResult?.catch?.((error: KustomerErrorResponse) => {
    // best-effort delivery marker; a stale/cross-session id 404s after a merge and must not escape as an unhandled rejection
    if (error?.status === '404') {
      debug({
        message: 'Session messages not found; skipping delivered marker',
      });
      return;
    }
    handleError(error, { message: 'Marking messages as delivered' });
  });
};

export const handleChatMessageReceivedEvent = (
  messageEvent: Pubnub.MessageEvent,
) => {
  const { message } = messageEvent;

  const { attachments, session } = message.data.relationships;
  const messageData = message.data;
  const { included } = message;
  const { id: messageId, attributes } = messageData;
  const { firstRead } = attributes;

  const sessionId = session.data.id;
  const { direction } = attributes;

  // Create unique key for deduplication using messageId and timetoken
  const messageKey = `${messageId}:${messageEvent.timetoken}`;

  // Check if this message has already been processed to prevent duplicate unread count increments
  if (processedMessages.has(messageKey)) {
    return;
  }

  // Mark message as processed
  processedMessages.add(messageKey);

  // Determine if we should increment unread count based on direction and firstRead status
  // 'out' = message from user to customer (incoming to customer) → increment
  // 'in' = message from customer to user (outgoing from customer) → don't increment
  // Also check that firstRead is not defined or is empty (no properties)
  const isFirstReadEmpty = !firstRead || Object.keys(firstRead).length === 0;
  const shouldIncrementUnreadCount = direction === 'out' && isFirstReadEmpty;

  if (direction === 'out') {
    markMessagesAsDelivered([messageEvent], sessionId);
  }

  if (attachments?.data?.length) {
    delayFetchMessageAttachments(messageId).then((response) => {
      const attachmentData = response.data;
      runListenersForOnMessageReceivedEvent(
        messageData,
        attachmentData,
        included,
        shouldIncrementUnreadCount,
      );
    });

    return;
  }

  runListenersForOnMessageReceivedEvent(
    messageData,
    undefined,
    included,
    shouldIncrementUnreadCount,
  );
};
